import { formatList, languageName, uiLocale } from '@boViews/shared/LanguageTabs/languageName';
import FlexBox from '@sharedComponents/FlexBox';
import Tag from '@sharedComponents/Tag';
import Tooltip from '@sharedComponents/Tooltip';
import Typography from '@sharedComponents/Typography';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { overriddenLanguages } from '../overrideRules';

import * as style from './style.module.css';

/**
 * Badge of an override text (i18n JSON string, or null): "Overridden · X/Y" in lavender, X being its filled
 * languages and Y the `total` of the catalogue languages, or "Inherited" in neutral without a filled language.
 * `field` ('title' or 'description') names the field in the label of an overridden text. With `withLanguages`,
 * an overridden badge takes the focus and lists its languages in a tooltip, shown on hover and on focus, which
 * is also the description of the badge for screen readers.
 */
const OverrideBadge = ({ text, total, field = null, withLanguages = false }) => {
    const { t, i18n } = useTranslation('bo');
    const descriptionId = useId();
    const codes = overriddenLanguages(text);

    if (codes.length === 0) {
        return (
            <Tag color="neutral" size="xs" fontSize="xs">
                {t('Inherited')}
            </Tag>
        );
    }

    const overridden = codes.length;
    let label = t('Overridden · {{overridden}}/{{total}}', { overridden, total });
    if (field === 'title') label = t('Title overridden · {{overridden}}/{{total}}', { overridden, total });
    if (field === 'description') label = t('Description overridden · {{overridden}}/{{total}}', { overridden, total });
    const badge = (
        <Tag color="lavender" size="xs" fontSize="xs">
            {label}
        </Tag>
    );
    if (!withLanguages) return badge;

    const locale = uiLocale(i18n);
    const languages = t('Overridden languages: {{languages}}', {
        languages: formatList(
            codes.map((code) => languageName(code, locale)),
            locale,
        ),
    });
    return (
        <Tooltip text={languages} popupIcon={false} position="top">
            <FlexBox role="img" tabIndex={0} aria-label={label} aria-describedby={descriptionId} className={style.trigger}>
                {badge}
                <Typography span hidden id={descriptionId}>
                    {languages}
                </Typography>
            </FlexBox>
        </Tooltip>
    );
};

export default OverrideBadge;
